import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { Message } from '@gonfalon/messages/Message';
import { AccessCheck } from '@gonfalon/permissions/access-check';
import { Box } from '@launchpad-ui/box';
import { Button } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import styles from './ProjectEmptyViewsState.module.css';

export type ProjectEmptyViewsStateProps = {
  onCreateView: () => void;
  /**
   * When true, the `Create view` button is suppressed (e.g. for filter contexts
   * that also pass `hideCreateButton` to the dialog). Heading + helper text
   * still render.
   */
  hideCreateButton?: boolean;
};

/**
 * Empty state rendered inside `ViewsDialog` / `RichViewsDialog` when the
 * project has zero views AND no search filter is active. Distinct from the
 * search no-match state (which keeps the existing "No views found" copy).
 */
export function ProjectEmptyViewsState({ onCreateView, hideCreateButton = false }: ProjectEmptyViewsStateProps) {
  const project = useProjectContext({ optional: true })?.project;

  return (
    <Box className={styles.container}>
      <p className={styles.heading}>
        <Message id="views.viewsDialog.noViews" />
      </p>
      <p className={styles.helper}>
        <Message id="views.viewsDialog.useViewsTo" />
      </p>
      {!hideCreateButton && project && (
        <AccessCheck resourceType="view" action="createView" project={project} hideControlIfNotAllowed>
          <Button variant="primary" size="small" className={styles.button} onPress={onCreateView}>
            <Icon name="add" size="small" />
            <span>
              <Message id="views.viewsDialog.createView" />
            </span>
          </Button>
        </AccessCheck>
      )}
    </Box>
  );
}
